import { RequestProvider } from '@/shared/providers/request.provider';
import { CLASSES_API, API_VARIABLES } from '../values/classes.values';
import { ClassStaffResponseDto } from '../models/classes.model';
import { BUILDER_STATUS, BUILDER_SYNC_STATUS } from '@/courses/values/courses.values';
import { ScheduleClassResponse } from '@/tools/interfaces/schedule-class-response.interface';

class ClassesServiceClass {

  async getAllClasses() {
    return RequestProvider.get(CLASSES_API.GET_ALL_CLASSES);
  }

  getClass(classId): Promise<ScheduleClassResponse> {
    let req = CLASSES_API.GET_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.get(req)
  }

  getDepartmentByOrganizationId(orgId, sortBy?: string) {
    const req = CLASSES_API.GET_INSTITUTION_DEPARTMENT.replace(API_VARIABLES.ORGANIZATION_ID, orgId.toString())
    return RequestProvider.get(req);
  }

  getClassesByDepartment(departmentId: number, page: number = 0, pageSize: number = 100, sortBy: string) {
    const req = CLASSES_API.GET_CLASSES_BY_DEPARTMENT.replace(API_VARIABLES.DEPARTMENT_ID, departmentId.toString())
      .replace(API_VARIABLES.PAGE, page.toString())
      .replace(API_VARIABLES.PAGE_SIZE, pageSize.toString())
      .replace(API_VARIABLES.SORT_BY, sortBy);
    return RequestProvider.get(req);
  }

  getInactiveClassesByDepartment(departmentId: number, page: number = 0, pageSize: number = 100, sortBy: string) {
    const req = CLASSES_API.GET_INACTIVE_CLASSES_BY_DEPARTMENT.replace(API_VARIABLES.DEPARTMENT_ID, departmentId.toString())
      .replace(API_VARIABLES.PAGE, page.toString())
      .replace(API_VARIABLES.PAGE_SIZE, pageSize.toString())
      .replace(API_VARIABLES.SORT_BY, sortBy);
    return RequestProvider.get(req);
  }

  getStudentsByClassId(classId:number){
    const req = CLASSES_API.GET_CLASS_STUDENTS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.get(req);
  }

  getStudentsByOrganizationId(orgId:number, orderByClassId:number, page:number=0, pageSize:number=100){
    const req = CLASSES_API.GET_ORGANIZATION_STUDENTS.replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString());
    return RequestProvider.get(req, {
      page,
      pageSize,
      orderByClassId
    });
  }

  getStudentsByOrganizationIdAndClass(orgId:number, classIds:string,personFilter:string, page:number=0, pageSize:number=100){
    const req = CLASSES_API.GET_ORGANIZATION_CLASS_STUDENTS.replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString());
    return RequestProvider.get(req, {
      page,
      pageSize,
      classIds,
      personFilter
    });
  }

  getClassesByOrganization(orgId:number, userId:number, search:string, page:number=0, pageSize:number=100){
    const req = CLASSES_API.GET_CLASSES_BY_ORG.replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString());
    return RequestProvider.get(req, {
      page,
      pageSize,
      userId,
      search
    });
  }

  getTeachersByClassId(classId: number): Promise<ClassStaffResponseDto[]> {
    return RequestProvider.get(
      CLASSES_API.GET_CLASS_TEACHERS
        .replace(API_VARIABLES.CLASS_ID,classId.toString())
    );
  }

  getTeachersByOrganizationId(orgId:number,orderByClassId:number,page:number=0,pageSize:number=100) {
    const req = CLASSES_API.GET_ORGANIZATION_TEACHERS.replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString());
    return RequestProvider.get(req, {
      page,
      pageSize,
      orderByClassId
    });
  }

  async getCoursesByClassId(classId:number, typesContent:string = 'CS,CSQ', builderContent: Array<any> = Array<any>()) {
    const response = await RequestProvider.get(CLASSES_API.GET_CLASS_COURSES
      .replace(API_VARIABLES.CLASS_ID,classId.toString())
      .replace(API_VARIABLES.TYPES_CONTENT,typesContent.toString()));
    
    let contentBuilderMaps = null;
    if(builderContent.length > 0) {
      contentBuilderMaps = new Map<number, any>(
        builderContent.filter(item => item.contentId !== undefined).map(item => [item.contentId as number, item])
      );
    }
    
    response.content.forEach(serverContent => {
        if(contentBuilderMaps && contentBuilderMaps.has(serverContent.id)) {
          let localContent = contentBuilderMaps.get(serverContent.id);
          if(serverContent.version && localContent.version !== serverContent.version && localContent.status != BUILDER_STATUS.PENDING) {
            localContent.status = BUILDER_STATUS.CONFLICT;
          }
          localContent.owner = serverContent.owner;
          localContent.originTool = serverContent.originTool;
          localContent.type = serverContent.type;
          localContent.courseTeachers = serverContent.courseTeachers;
          localContent.hasGrading = serverContent.hasGrading;
          localContent.locked = serverContent.locked;
          localContent.hasLocalBuilder = true;
        } else {
          serverContent.syncStatus = BUILDER_SYNC_STATUS.READY_DOWNLOAD;
          serverContent.hasLocalBuilder = false;
          builderContent.push(serverContent);
        }
      });

    return builderContent;
  }

  getCoursesByOrganizationId(orgId: number, body) {
    return RequestProvider.get(CLASSES_API.GET_ORGANIZATION_COURSES
      .replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString()), body);
  }

  searchUsersInOrganization(orgId:number, keyword: string, userTypes: string) {
    const req = CLASSES_API.SEARCH_USERS_ORGANIZATION.replace(API_VARIABLES.ORGANIZATION_ID,orgId.toString());
    return RequestProvider.get(req, {
      keyword,
      userTypes,
      pageSize: -1
    });
  }

  getCourseConfig(classId: number, courseId: number) {
    const req = CLASSES_API.GET_UPDATE_COURSE_CONFIG
      .replace(API_VARIABLES.CLASS_ID,classId.toString())
      .replace(API_VARIABLES.COURSE_ID, courseId.toString());
    return RequestProvider.get(req);
  }

  getSharedTeachers(courseId: number,page: number,pageSize: number) {
    const req = CLASSES_API.GET_SHARED_TEACHERS.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    return RequestProvider.get(req, {
      page,
      pageSize
    });
  }

  async canSubstituteTeacherEditClassId(classId:number): Promise<any> {
    const req = CLASSES_API.CAN_SUBSTITUTE_TEACHER_EDIT_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    try {
      const  { canEdit } = await RequestProvider.get(req);
      const res = canEdit.data !== undefined ? canEdit.data : canEdit;
      return res;
    } catch(error) {
      return false
    }
  }

  async getCourseTemplateByClass(templateId: number, classId: number) : Promise<any>  {
    const req = CLASSES_API.GET_COURSE_TEMPLATE_BY_CLASS
      .replace(API_VARIABLES.TEMPLATE_ID, String(templateId))
      .replace(API_VARIABLES.CLASS_ID, String(classId));
    return await RequestProvider.get(req);
  }

  async getQuickContent(templateId: number, quickContentId: number) {
    const req = CLASSES_API.GET_QUICK_CONTENT
      .replace(API_VARIABLES.TEMPLATE_ID, String(templateId))
      .replace(API_VARIABLES.QUICK_CONTENT_ID, String(quickContentId));
    return await RequestProvider.get(req); 
  }

  async getTeachingClassesAndSharedContents(personId:number, orgId:number){
    const req = CLASSES_API.GET_TEACHING_CLASSES_AND_SHARED_CONTENTS
      .replace(API_VARIABLES.PERSON_ID, String(personId))
      .replace(API_VARIABLES.ORGANIZATION_ID, String(orgId));
    return await RequestProvider.get(req); 
  }

  /* POST */
  createClass(data) {
    let req = CLASSES_API.CREATE_CLASS;
    return RequestProvider.post(req, data);
  }

  addStudentToClass(classId, body) {
    let req = CLASSES_API.ADD_REMOVE_STUDENT_TO_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.post(req, body);
  }
  
  addTeacherToClass(classId, body) {
    let req = CLASSES_API.ADD_REMOVE_TEACHER_TO_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.post(req, body)
  }

  addCourseToClass(classId, body) {
    let req = CLASSES_API.ADD_REMOVE_COURSE_TO_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.post(req, body)
  }

  updateMainTeacher(classId:number, personId:number) {
    let req = CLASSES_API.UPDATE_TEACHER_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    req = req.replace(API_VARIABLES.PERSON_ID,personId.toString());
    return RequestProvider.post(req)
  }

  updateCourseConfig(classId, courseId, body) {
    let req = CLASSES_API.GET_UPDATE_COURSE_CONFIG.replace(API_VARIABLES.CLASS_ID,classId.toString());
    req = req.replace(API_VARIABLES.COURSE_ID, courseId.toString());
    return RequestProvider.post(req, body)
  }

  async createQuickContent({
    templateId, 
    classId,
    schoolDay, 
    subject, 
    coverImage, 
    automaticFolderType,
    componentsSelected = []
  }) {
    const req = CLASSES_API.CREATE_QUICK_CONTENT
      .replace('{templateId}', String(templateId))
      .replace('{classId}', String(classId));

    const formData = new FormData();
    const quickContentRequest = JSON.stringify({
      schoolDay,
      subject, 
      automaticFolderType,
      componentsSelected,
    });

    formData.append('quickContentRequest', quickContentRequest);
    formData.append('coverImage', coverImage);

    return await RequestProvider.post(
      req,
      formData,
      {},
      {
        'Content-Type': 'multipart/form-data'
      }
    );
  }

  async createSubjectForBuilderA({
    templateId, 
    classId,
    schoolDay, 
    subject, 
    coverImage, 
    automaticFolderType,
    componentsSelected = []
  }) {
    const req = CLASSES_API.CREATE_SUBJECT_FOR_BUILDER
      .replace('{templateId}', String(templateId));

    const formData = new FormData();
    const contentRequest = JSON.stringify({
      schoolDay,
      subject, 
      automaticFolderType,
      componentsSelected,
    });

    formData.append('contentRequest', contentRequest);
    formData.append('coverImage', coverImage);

    return await RequestProvider.post(
      req,
      formData,
      {classId: (classId) ? String(classId) : null},
      {
        'Content-Type': 'multipart/form-data'
      }
    );
  }

  /* PUT */
  updateClass(classId, body) {
    let req = CLASSES_API.UPDATE_DELETE_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.put(req, body)
  }

  updateCourseOwner(courseId, personId) {
    let req = CLASSES_API.UPDATE_COURSE_OWNER.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    req = req.replace(API_VARIABLES.PERSON_ID, personId.toString());
    return RequestProvider.post(req)
  }

  shareCourse(courseId, personId) {
    let req = CLASSES_API.SHARE_UNSHARE_COURSE.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    req = req.replace(API_VARIABLES.PERSON_ID, personId.toString());
    return RequestProvider.put(req)
  }

  unshareCourse(courseId, personId) {
    let req = CLASSES_API.SHARE_UNSHARE_COURSE.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    req = req.replace(API_VARIABLES.PERSON_ID, personId.toString());
    return RequestProvider.remove(req)
  }

  blockCourse(courseId) {
    let req = CLASSES_API.BLOCK_UNBLOCK_COURSE.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    return RequestProvider.put(req)
  }

  unblockCourse(courseId) {
    let req = CLASSES_API.BLOCK_UNBLOCK_COURSE.replace(API_VARIABLES.COURSE_ID,courseId.toString());
    return RequestProvider.remove(req)
  }

  allowTeachersToEdit(classId) {
    const req = CLASSES_API.ALLOW_TEACHERS_TO_EDIT_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.put(req)
  }

  async updateQuickContent({
    quickContentId,
    templateId, 
    classId,
    schoolDay, 
    subject, 
    coverImage, 
    automaticFolderType,
    componentsSelected = []}) {
    const req = CLASSES_API.UPDATE_QUICK_CONTENT
      .replace(API_VARIABLES.TEMPLATE_ID, String(templateId))
      .replace(API_VARIABLES.QUICK_CONTENT_ID, String(quickContentId));
    let formData = new FormData();
    let quickContentRequest = JSON.stringify({
      templateId,
      classId,
      schoolDay,
      subject, 
      automaticFolderType,
      componentsSelected});
    formData.append('quickContentRequest', quickContentRequest);
    formData.append('coverImage', coverImage);
    return await RequestProvider.put(req, formData, {}, { 'Content-Type': 'multipart/form-data' });
  }

  /* DELETE */
  deleteClass(classId: number){
    let req = CLASSES_API.UPDATE_DELETE_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.remove(req)
  }

  inactivateClass(classId: number){
    let req = CLASSES_API.INACTIVATE_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.put(req)
  }

  removeStudentFromClass(classId, body){
    let req = CLASSES_API.ADD_REMOVE_STUDENT_TO_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.remove(req, body)
  }

  removeTeacherFromClass(classId, body){
    let req = CLASSES_API.ADD_REMOVE_TEACHER_TO_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.remove(req, body)
  }

  removeCourseFromClass(classId, body){
    let req = CLASSES_API.ADD_REMOVE_COURSE_TO_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.remove(req, body)
  }

  removeCourseTeacherFromClass(classId, body){
    let req = CLASSES_API.REMOVE_COURSE_TEACHER_FROM_CLASS.replace(API_VARIABLES.CLASS_ID, classId.toString());
    return RequestProvider.remove(req, body)
  }

  disallowTeachersToEdit(classId) {
    const req = CLASSES_API.ALLOW_TEACHERS_TO_EDIT_CLASS.replace(API_VARIABLES.CLASS_ID,classId.toString());
    return RequestProvider.remove(req)
  }

  async removeQuickContent(quickContentId: number, classId: number) : Promise<any>  {
    const req = CLASSES_API.REMOVE_QUICK_CONTENT
      .replace('{quickContentId}', String(quickContentId));

    return await RequestProvider.remove(req);
  }
}


export const ClassesService = new ClassesServiceClass();